import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { finalize, mergeMap, delay } from 'rxjs/operators';
import { retryWhen } from 'rxjs/operators';

@Injectable()
export class RetryInterceptor implements HttpInterceptor {

  private urlsToRetry: string[] = [];

  // Higher retry count accommodates intermittent cloud/network failures without requiring manual user intervention.
  private readonly maxRetries = 10;

  // A short initial delay lets transient blips resolve quickly before backing off.
  private readonly baseDelayMs = 500;

  // Cap prevents exponential backoff from producing unreasonably long waits between attempts.
  private readonly maxDelayMs = 5000;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (!req.headers.has('noretry')) {
      this.urlsToRetry.push(req.url);
    }

    const modifiedReq = req.clone({
      // avoid need to enable backend having CORS policy to allow this header
      headers: req.headers.delete('noretry')
    });

    return next.handle(modifiedReq).pipe(
      retryWhen(err => this.handleRetryOnError(err, req)),
      finalize(() => this.urlsToRetry = [...this.urlsToRetry.filter(x => x !== req.url)]));
  }

  private handleRetryOnError(errors: Observable<any>, req: HttpRequest<any>): Observable<any> {

    if (!this.urlsToRetry.includes(req.url)) {
      return throwError(() => errors);
    }

    return errors.pipe(
      mergeMap((err: HttpResponse<any>, count: number) => {
        if (count >= this.maxRetries || !this.isRetryableError(err)) {
          return throwError(() => err);
        }
        return of(err).pipe(delay(this.getBackoffDelay(count)));
      })
    );
  }

  // Exponential backoff with jitter: 500-750ms, 1-1.5s, 2-3s, … capped at 5s.
  // Jitter staggers concurrent clients to avoid thundering-herd retries against the same endpoint.
  private getBackoffDelay(retryCount: number): number {
    const exponentialDelay = this.baseDelayMs * Math.pow(2, retryCount);
    const jitter = exponentialDelay * (0.5 * Math.random());
    return Math.min(exponentialDelay + jitter, this.maxDelayMs);
  }

  // Retryable: network errors (status 0), server errors (5xx), and specific 4xx codes
  // that indicate transient conditions (408 Request Timeout, 429 Too Many Requests).
  private isRetryableError(err: HttpResponse<any>): boolean {
    const status = err?.status;
    if (status === undefined || status === 0) return true; // Network error / CORS blocked
    if (status === 408 || status === 429) return true;     // Transient 4xx
    if (status >= 500) return true;                        // Server errors
    return false;
  }
}
